import * as Yup from 'yup';
import Swal from 'sweetalert2';

const FIELD_LABELS = {
  name: 'Item Name',
  requestsSupported: 'Supported Request Types',
  make: 'Make / Brand',
  modelNo: 'Model Number',
  sku: 'SKU(s)',
  costPerUnit: 'Cost Per Unit',
  'quantity.active': 'Active Quantity',
  'quantity.min': 'Minimum Quantity',
  'quantity.max': 'Maximum Quantity',
  'locationDetails.room': 'Room / Location',
  'locationDetails.notes': 'Location Notes',
  'warrantyDetails': 'Warranty Details',
  'warrantyDetails.boughtOn': 'Warranty Bought On',
  'warrantyDetails.boughtFrom': 'Warranty Bought From',
  'warrantyDetails.warrantyExpirationDate': 'Warranty Expiration Date',
  images: 'Images',
  invoices: 'Invoices',
  'property_details.property_id': 'Property',
  'property_details.building_name': 'Building Name',
  property_details: 'Property Details',
};

const prettifyPath = (path = '') => {
  if (!path) return 'Field';
  return (
    FIELD_LABELS[path] ||
    path
      .split('.')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).replace(/_/g, ' '))
      .join(' › ')
  );
};

const humanizeMessage = (message = '') => {
  if (!message) return 'This field is required.';
  if (/must be a `number` type/i.test(message)) return 'Please enter a valid number.';
  if (/must be greater than or equal to/i.test(message)) return 'Value cannot be negative.';
  if (/cannot be null/i.test(message)) return 'This field cannot be empty.';
  if (/is a required field/i.test(message)) return 'This field is required.';
  if (/must be at least/i.test(message)) return 'At least one item is required.';
  if (/must be an array/i.test(message)) return 'Invalid format — expected a list.';
  return message.charAt(0).toUpperCase() + message.slice(1);
};

const GROUP_META = {
  'Property': { icon: '🏢', color: '#0891B2', bg: '#ECFEFF', border: '#22D3EE' },
  'Basic Info': { icon: '📦', color: '#6366F1', bg: '#EEF2FF', border: '#818CF8' },
  'Quantity & Cost': { icon: '💰', color: '#0EA5E9', bg: '#F0F9FF', border: '#38BDF8' },
  'Location': { icon: '📍', color: '#059669', bg: '#ECFDF5', border: '#6EE7B7' },
  'Warranty': { icon: '🛡️', color: '#7C3AED', bg: '#F5F3FF', border: '#A78BFA' },
  'Media': { icon: '📸', color: '#D97706', bg: '#FFFBEB', border: '#FCD34D' },
  'Other': { icon: '⚙️', color: '#B45309', bg: '#FFFBEB', border: '#F59E0B' },
};

const GROUP_KEYS = Object.keys(GROUP_META);

const groupErrors = errors => {
  const groups = Object.fromEntries(GROUP_KEYS.map(k => [k, []]));
  for (const { path, message } of errors) {
    const item = { label: prettifyPath(path), message: humanizeMessage(message) };
    if (path.includes('property_details')) groups['Property'].push(item);
    else if (['name', 'requestsSupported', 'make', 'modelNo', 'sku'].includes(path)) groups['Basic Info'].push(item);
    else if (path.startsWith('quantity.') || path === 'costPerUnit') groups['Quantity & Cost'].push(item);
    else if (path.startsWith('locationDetails.')) groups['Location'].push(item);
    else if (path === 'warrantyDetails' || path.startsWith('warrantyDetails')) groups['Warranty'].push(item);
    else if (['images', 'invoices'].includes(path)) groups['Media'].push(item);
    else groups['Other'].push(item);
  }
  return groups;
};

const showValidationErrors = async (groups, totalCount) => {
  const active = GROUP_KEYS.filter(k => groups[k]?.length > 0);

  const chipsHtml = active.map(key => {
    const m = GROUP_META[key];
    return `
      <span style="display:inline-flex;align-items:center;gap:5px;padding:4px 11px;border-radius:99px;
        white-space:nowrap;font-size:11.5px;font-weight:600;
        color:${m.color};background:${m.bg};border:1.5px solid ${m.border};">
        ${m.icon} ${key}
        <span style="background:${m.color};color:#fff;border-radius:99px;padding:1px 7px;font-size:10px;font-weight:700;">
          ${groups[key].length}
        </span>
      </span>`;
  }).join('');

  const cardsHtml = active.map(key => {
    const m = GROUP_META[key];
    const items = groups[key];
    const shown = items.slice(0, 6);
    const extra = items.length - 6;

    const rowsHtml = shown.map((item, i) => `
      <div style="display:flex;align-items:flex-start;gap:10px;background:#fff;border-radius:9px;
        padding:9px 13px;border:1px solid #E2E8F0;box-shadow:0 1px 3px rgba(0,0,0,.04);margin-bottom:7px;">
        <div style="flex-shrink:0;width:20px;height:20px;border-radius:50%;background:#EF4444;color:#fff;
          font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;margin-top:1px;">
          ${i + 1}
        </div>
        <div>
          <div style="font-size:12.5px;font-weight:600;color:#1E293B;line-height:1.4;margin-bottom:2px;">${item.label}</div>
          <div style="font-size:12px;color:#EF4444;font-weight:500;">→ ${item.message}</div>
        </div>
      </div>`).join('');

    const overflowHtml = extra > 0
      ? `<div style="text-align:center;font-size:11.5px;color:#94A3B8;font-style:italic;padding:2px 0 6px;">+ ${extra} more in this section…</div>`
      : '';

    return `
      <div style="border-radius:12px;overflow:hidden;margin-bottom:10px;
        border:1.5px solid ${m.border};box-shadow:0 2px 8px rgba(0,0,0,.04);">
        <div style="background:${m.bg};color:${m.color};display:flex;align-items:center;gap:9px;
          padding:11px 16px;font-weight:700;font-size:13.5px;border-bottom:1px solid ${m.border};">
          <span style="font-size:16px;">${m.icon}</span>
          ${key}
          <span style="margin-left:auto;font-size:11px;font-weight:700;padding:2px 9px;border-radius:99px;background:rgba(0,0,0,.08);">
            ${items.length} issue${items.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div style="padding:10px 14px 4px;">${rowsHtml}${overflowHtml}</div>
      </div>`;
  }).join('');

  const html = `
    <style>
      .ri-vem { font-family:'Inter',system-ui,sans-serif; color:#0F172A; text-align:left;
        max-height:60vh; overflow-y:auto; padding-right:4px;
        scrollbar-width:thin; scrollbar-color:#CBD5E1 transparent; }
      .ri-vem::-webkit-scrollbar { width:5px; }
      .ri-vem::-webkit-scrollbar-thumb { background:#CBD5E1; border-radius:99px; }
    </style>
    <div class="ri-vem">
      <div style="background:linear-gradient(135deg,#0F1C2E 0%,#1D4ED8 60%,#1A2F4B 100%);
        border-radius:14px;padding:20px 22px;margin-bottom:18px;
        display:flex;align-items:center;gap:16px;box-shadow:0 8px 24px rgba(29,78,216,.25);">
        <div style="width:52px;height:52px;background:rgba(255,255,255,.15);border-radius:14px;
          display:flex;align-items:center;justify-content:center;font-size:26px;flex-shrink:0;">📦</div>
        <div style="flex:1;min-width:0;">
          <p style="margin:0;font-size:16px;font-weight:700;color:#fff;letter-spacing:-.3px;">
            Almost there — a few fields need attention
          </p>
          <p style="margin:3px 0 0;font-size:12.5px;color:rgba(255,255,255,.72);">
            Fix the highlighted items below and re-submit.
          </p>
        </div>
        <div style="background:#EF4444;color:#fff;font-size:13px;font-weight:700;padding:6px 14px;
          border-radius:99px;box-shadow:0 2px 10px rgba(239,68,68,.5);white-space:nowrap;flex-shrink:0;">
          ${totalCount} issue${totalCount !== 1 ? 's' : ''}
        </div>
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:18px;">${chipsHtml}</div>
      ${cardsHtml}
      <div style="background:#F8FAFC;border-radius:10px;border:1px solid #E2E8F0;
        padding:11px 14px;display:flex;align-items:flex-start;gap:9px;
        font-size:12px;color:#475569;margin-top:4px;margin-bottom:6px;">
        <span style="font-size:15px;flex-shrink:0;">💡</span>
        <span>Required fields are marked above. Ensure quantities are non-negative and at least one request type is selected.</span>
      </div>
    </div>`;

  await Swal.fire({
    html,
    confirmButtonText: '✏️  Fix Issues',
    confirmButtonColor: '#1D4ED8',
    showCancelButton: true,
    cancelButtonText: 'Dismiss',
    cancelButtonColor: '#94A3B8',
    width: 660,
    padding: '24px',
    background: '#F8FAFC',
  });
};

export const PropertyDetailsSchema = Yup.object().shape({
  property_id: Yup.string().required('Property selection is required'),
  building_name: Yup.string().trim().required('Building name is required'),
});

export const RecurringInventoryItemValidationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Item name is required')
    .min(2, 'Item name must be at least 2 characters'),

  requestsSupported: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one supported request type is required')
    .required('Supported request types are required'),

  make: Yup.string().trim().nullable(),
  modelNo: Yup.string().trim().nullable(),
  sku: Yup.array().of(Yup.string().trim()).nullable(),

  costPerUnit: Yup.number()
    .required('Cost per unit is required')
    .min(0, 'Cost per unit cannot be negative')
    .typeError('Please enter a valid number'),

  quantity: Yup.object().shape({
    active: Yup.number()
      .required('Active quantity is required')
      .min(0, 'Active quantity cannot be negative')
      .integer('Active quantity must be a whole number')
      .typeError('Please enter a valid number'),
    min: Yup.number()
      .min(0, 'Minimum quantity cannot be negative')
      .integer('Minimum quantity must be a whole number')
      .nullable(),
    max: Yup.number()
      .min(0, 'Maximum quantity cannot be negative')
      .integer('Maximum quantity must be a whole number')
      .nullable()
      .test(
        'max-gte-min',
        'Max quantity must be greater than or equal to min quantity',
        function (value) {
          const { min } = this.parent;
          return value == null || min == null || value >= min;
        }
      ),
  }),

  locationDetails: Yup.object().shape({
    room: Yup.string().trim().nullable(),
    notes: Yup.string().trim().nullable(),
  }),

  warrantyDetails: Yup.array()
    .of(
      Yup.object().shape({
        boughtOn: Yup.date()
          .nullable()
          .max(new Date(), 'Purchase date cannot be in the future'),
        boughtFrom: Yup.string()
          .trim()
          .required('Supplier / vendor name is required'),
        warrantyExpirationDate: Yup.date()
          .nullable()
          .test(
            'exp-after-bought',
            'Expiration date must be after purchase date',
            function (value) {
              const { boughtOn } = this.parent;
              if (!value || !boughtOn) return true;
              return new Date(value) >= new Date(boughtOn);
            }
          ),
      })
    )
    .nullable(),

  images: Yup.array().of(Yup.object().shape({ url: Yup.string().url().required() })).nullable(),
  invoices: Yup.array().of(Yup.object().shape({ url: Yup.string().url().required() })).nullable(),

  property_details: PropertyDetailsSchema.required('Property details are required'),
});

export const validateRecurringInventoryItem = async (values, context = {}) => {
  try {
    await RecurringInventoryItemValidationSchema.validate(values, { abortEarly: false, context });
    return true;
  } catch (err) {
    const errors = (err.inner || []).map(e => ({ path: e.path || '', message: e.message }));
    if (!errors.length) return true;
    await showValidationErrors(groupErrors(errors), errors.length);
    return false;
  }
};

export const getInlineErrors = async (values, context = {}) => {
  try {
    await RecurringInventoryItemValidationSchema.validate(values, { abortEarly: false, context });
    return {};
  } catch (err) {
    return Object.fromEntries(
      (err.inner || []).map(e => [e.path || '__root__', humanizeMessage(e.message)])
    );
  }
};

export const formatValidationErrors = errors =>
  Object.entries(errors).map(([field, message]) => `${prettifyPath(field)}: ${message}`);

export default RecurringInventoryItemValidationSchema;